import React, { useEffect, useState } from "react";
import { Plus, FileText, Trash2, ExternalLink, FileSignature } from "lucide-react";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { PageHeader, Card, Badge, Loading, Modal, fmtDate, EmptyState } from "../components/ui";

const LETTER_TYPES = [
  ["APPOINTMENT", "Appointment Letter"], ["CONFIRMATION", "Confirmation Letter"],
  ["RELIEVING", "Relieving Letter"], ["EXPERIENCE", "Experience Certificate"], ["ADDRESS_PROOF", "Address Proof Letter"],
];

export default function Documents() {
  const { user } = useAuth();
  const [items, setItems] = useState(null);
  const [emps, setEmps] = useState([]);
  const [open, setOpen] = useState(false);
  const [letterOpen, setLetterOpen] = useState(false);
  const [form, setForm] = useState({ employeeId: "", name: "", category: "Employment", url: "" });
  const [letter, setLetter] = useState({ employeeId: "", type: "EXPERIENCE" });
  const canManage = ["SUPERADMIN", "HR_ADMIN", "MANAGER"].includes(user?.role);
  const canAdmin = ["SUPERADMIN", "HR_ADMIN"].includes(user?.role);

  const load = () => api.get("/documents").then(({ data }) => setItems(data));
  useEffect(() => { load(); api.get("/employees?limit=100").then(({ data }) => setEmps(data.items)); }, []);
  const add = async (e) => { e.preventDefault(); await api.post("/documents", form); setOpen(false); setForm({ employeeId: "", name: "", category: "Employment", url: "" }); load(); };
  const del = async (id) => { if (window.confirm("Delete document?")) { await api.delete(`/documents/${id}`); load(); } };
  const genLetter = async (e) => {
    e.preventDefault();
    if (!letter.employeeId) return alert("Select an employee");
    try { await api.post("/documents/letter", letter); alert("Letter generated"); setLetterOpen(false); setLetter({ employeeId: "", type: "EXPERIENCE" }); load(); }
    catch (err) { alert(err.response?.data?.detail || "Failed"); }
  };
  const openDoc = async (d) => {
    if (d.url?.startsWith("/")) {
      try {
        const res = await api.get(d.url.replace(/^\/api/, ""), { responseType: "blob" });
        const blobUrl = URL.createObjectURL(res.data);
        window.open(blobUrl, "_blank");
      } catch { alert("Unable to open document"); }
    } else {
      window.open(d.url, "_blank");
    }
  };
  if (!items) return <Loading />;

  return (
    <div>
      <PageHeader title="Documents" subtitle="Employee documents & auto-generated HR letters">
        {canAdmin && <button data-testid="gen-letter" onClick={() => setLetterOpen(true)} className="btn-outline"><FileSignature size={16} /> Generate Letter</button>}
        {canManage && <button data-testid="add-document" onClick={() => setOpen(true)} className="btn-accent"><Plus size={16} /> Add Document</button>}
      </PageHeader>
      {items.length === 0 ? <EmptyState icon={FileText} title="No documents" /> : (
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {items.map((d) => (
            <Card key={d.id} interactive className="p-5" data-testid={`doc-${d.id}`}>
              <div className="flex items-start justify-between">
                <div className={`flex h-11 w-11 items-center justify-center rounded-lg ${d.category === "HR Letter" ? "bg-violet-100 text-violet-600 dark:bg-violet-500/15" : "bg-accent/10 text-accent"}`}>{d.category === "HR Letter" ? <FileSignature size={20} /> : <FileText size={20} />}</div>
                {canManage && <button onClick={() => del(d.id)} className="text-slate-300 hover:text-red-500"><Trash2 size={16} /></button>}
              </div>
              <h3 className="mt-3 font-semibold text-slate-800 dark:text-slate-100">{d.name}</h3>
              <p className="text-xs text-slate-400">{d.employeeName}</p>
              <div className="mt-3 flex items-center justify-between">
                <Badge tone="info">{d.category}</Badge>
                <button onClick={() => openDoc(d)} data-testid={`open-doc-${d.id}`} className="flex items-center gap-1 text-xs font-semibold text-accent hover:underline">Open <ExternalLink size={12} /></button>
              </div>
              <p className="mt-2 text-[11px] text-slate-400">{fmtDate(d.uploadedAt)}</p>
            </Card>
          ))}
        </div>
      )}

      <Modal open={open} onClose={() => setOpen(false)} title="Add Document"
        footer={<><button onClick={() => setOpen(false)} className="btn-outline">Cancel</button><button form="doc-form" className="btn-accent">Add</button></>}>
        <form id="doc-form" onSubmit={add} className="space-y-4">
          <div><label className="label">Employee *</label><select required data-testid="doc-emp" value={form.employeeId} onChange={(e) => setForm({ ...form, employeeId: e.target.value })} className="input"><option value="">— Select —</option>{emps.map((e) => <option key={e.id} value={e.id}>{e.fullName}</option>)}</select></div>
          <div className="grid grid-cols-2 gap-4">
            <div><label className="label">Name *</label><input required data-testid="doc-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="input" /></div>
            <div><label className="label">Category</label><input data-testid="doc-cat" value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} className="input" /></div>
          </div>
          <div><label className="label">Document URL *</label><input required type="url" data-testid="doc-url" value={form.url} onChange={(e) => setForm({ ...form, url: e.target.value })} className="input" placeholder="https://…" /></div>
        </form>
      </Modal>

      <Modal open={letterOpen} onClose={() => setLetterOpen(false)} title="Generate HR Letter"
        footer={<><button onClick={() => setLetterOpen(false)} className="btn-outline">Cancel</button><button form="letter-form" className="btn-accent">Generate PDF</button></>}>
        <form id="letter-form" onSubmit={genLetter} className="space-y-4">
          <div><label className="label">Employee *</label><select required data-testid="letter-emp" value={letter.employeeId} onChange={(e) => setLetter({ ...letter, employeeId: e.target.value })} className="input"><option value="">— Select —</option>{emps.map((e) => <option key={e.id} value={e.id}>{e.fullName}</option>)}</select></div>
          <div><label className="label">Letter Type *</label><select data-testid="letter-type" value={letter.type} onChange={(e) => setLetter({ ...letter, type: e.target.value })} className="input">{LETTER_TYPES.map(([v, l]) => <option key={v} value={v}>{l}</option>)}</select></div>
          <p className="text-xs text-slate-400">The letter is auto-numbered (SMIFS/HR/…) and populated from the employee's designation, department, joining date and CTC. It is saved to their document repository as a downloadable PDF.</p>
        </form>
      </Modal>
    </div>
  );
}
